/**
 * assets/wc-pdp-swap.js
 * Post-modal-close child-PDP "feel" subsystem — ported from monolith.
 *
 * Monolith line ranges lifted:
 *   1225–1228   getReopenPill (local helper)
 *   1234–1254   hasAnyStoredGenForCurrentPdp (local helper)
 *   1251–1261   refreshReopenPill
 *   1263–1282   reopenLastResult
 *   2148–2326   swapPdpForGeneration
 *   2327–2396   applyCleanBreakNow, applyCleanBreakDeferred, flushDeferredSwap
 *   2447–2461   _swapGalleryImages (thin shim → renderUnifiedGallery)
 *   2463–2530   _swapDescriptionAndJsonLd
 *   2613–2616   getProgressPill (local helper)
 *   2617–2631   refreshProgressPill
 *   2636–2675   minimizeModalToPill (minimal version per task spec)
 *   5589–5601   getCurrentShopifyHandle (inlined as local helper)
 *
 * WC.foo → window.WC.foo throughout (no local IIFE alias in ES modules).
 * Cross-cutting deps imported from sibling modules; typeof-guards kept only
 * where the dep could be absent at the call site (e.g. initUploadZoneState).
 */

import { log, track } from './wc77-analytics.js';
import { getModal, setModalState } from './wc2-modal.js';
import {
  renderUnifiedGallery,
  updatePdpVariantState,
  CANVAS_PRICING,
  CANVAS_FRAMED_PRICING,
  PAINT_KIT_PRICING,
} from './wc77-selectors.js';
// showResult is imported lazily inside reopenLastResult to avoid a
// module-init-time circular edge (wc7-result → wc-pdp-swap → wc7-result).
// All ported functions are called post-init, never at module evaluation time,
// so ES module live-binding safety is maintained.

// ─── Module-level state (mirrors WC._ globals initialised by monolith) ─────
// These are the only three the monolith initialises outside a function.
// In v2-only mode we initialise them here so applyCleanBreakNow/Deferred
// have somewhere to write even before window.WC sets them.
if (typeof window !== 'undefined' && window.WC) {
  if (window.WC._lastSwappedHandle === undefined)   window.WC._lastSwappedHandle  = null;
  if (window.WC._pendingCleanBreak === undefined)   window.WC._pendingCleanBreak  = false;
  if (window.WC._descriptionFetchInflight === undefined) window.WC._descriptionFetchInflight = null;
  if (window.WC._minimized === undefined)           window.WC._minimized          = false;
}

// ─── Local DOM helpers ────────────────────────────────────────────────────────

function getReopenPill() {
  return document.querySelector('[data-wc-component="reopen-pill"]');
}

function getProgressPill() {
  return document.querySelector('[data-wc-component="progress-pill"]');
}

// ─── hasAnyStoredGenForCurrentPdp ─────────────────────────────────────────────
// Monolith lines 1234–1254.
function hasAnyStoredGenForCurrentPdp() {
  var wc = window.WC;
  if (!wc || !wc.gens || typeof wc.gens.list !== 'function') return false;
  var zones = document.querySelectorAll('[data-wc-component="upload-zone"]');
  for (var i = 0; i < zones.length; i++) {
    var z = zones[i];
    var product = {
      productHandle: z.getAttribute('data-wc-product-handle'),
      productType:   z.getAttribute('data-wc-product-type'),
      artId:         z.getAttribute('data-wc-product-art-id') || null,
    };
    var key = wc.gens.getNamespaceKey(product);
    var gens = key ? wc.gens.list(key) : [];
    if (gens && gens.length > 0) return true;
  }
  return false;
}

// ─── getCurrentShopifyHandle (local helper) ──────────────────────────────────
// Inlined from monolith lines 5589–5601 (monolith-only; not exported there).
function getCurrentShopifyHandle() {
  var R = window.WC._currentResult;
  if (!R) return null;
  if (R.activeFormat === 'canvas') {
    var c = R.canvasShopify && R.canvasShopify[R.activeCanvasSize];
    return c && c.shopifyHandle ? c.shopifyHandle : null;
  }
  if (R.activeFormat === 'canvas_framed') {
    var f = R.canvasFramedShopify && R.canvasFramedShopify[R.activeCanvasFramedSize];
    return f && f.shopifyHandle ? f.shopifyHandle : null;
  }
  return (R.paintKitShopify && R.paintKitShopify.shopifyHandle) || null;
}

// ─── _swapGalleryImages ───────────────────────────────────────────────────────
// Monolith lines 2447–2461 (thin shim post-v7; routes through renderUnifiedGallery).
function _swapGalleryImages(/* R, format */) {
  // 2026-05-14 v7 — All formats route through renderUnifiedGallery.
  // _swapGalleryImages is kept as a call-site shim; the legacy rotation
  // path was removed in v7 because it conflicted with the slot model.
  try { renderUnifiedGallery(); } catch (e) { /* non-fatal */ }
}

// ─── _setVisibleProductTitle ──────────────────────────────────────────────────
// 2026-07-02 QA round-1 fix — the old title write queried h1 variants only,
// but this theme (Horizon) renders the product title as an <a class="link">
// inside .view-product-title (blocks/_product-details.liquid) and/or via the
// product-title block ([ref="productTitleLink"] wrapping a text element) —
// never an h1. So after a format clean-break the visible title stayed the
// parent's ("… Canvas Print" on a paint-kit child URL) and document.title was
// never written at all. Update every real title carrier on the page,
// excluding recommendation cards, and keep the tab title in step.
// 2026-07-02 QA round-2 fix (R2-2) — the CUSTOMER-VISIBLE title on this
// template is a bare <h3> (rich-text block inside the product-information
// section); the .view-product-title carrier exists but is hidden here, so the
// visible heading kept the parent's title after a clean-break swap.
// 2026-07-02 QA round-4 (deploy finding) — Shopify's template sanitizer
// 422-rejects `data-wc-product-title` on that h3 in the template JSON, so
// the attribute hook CANNOT ship and the template hunks were reverted. The
// SCOPED ".product-information h3" selector is therefore the PRIMARY hook
// (proven working in-browser during round-3/round-4 QA). The
// [data-wc-product-title] selector stays first in the query list as a
// graceful upgrade if the attribute ever lands by other means (section
// markup, theme editor). Wolden component h3s (flow upload headings,
// generation takeover) are excluded below.
function _setVisibleProductTitle(title) {
  if (!title) return;
  try {
    var candidates = document.querySelectorAll(
      '[data-wc-product-title], ' +
      '.product-information h3, ' +
      '.view-product-title .link, ' +
      '[ref="productTitleLink"] h1, [ref="productTitleLink"] h2, ' +
      '[ref="productTitleLink"] h3, [ref="productTitleLink"] h4, ' +
      '[ref="productTitleLink"] p, ' +
      'main h1, .product-information h1, .product-title h1, h1.product-title'
    );
    var wrote = false;
    candidates.forEach(function (el) {
      // Never touch related-products / recommendation cards.
      if (el.closest('.product-recommendations, .product-recommendation, product-recommendations, [data-section-type*="recommend"], [data-section-type*="related"]')) return;
      // Never touch headings inside Wolden components (flow upload heading,
      // generation takeover title, modal) — they share the section but are
      // NOT product-title carriers. The data-wc-product-title hook itself
      // lives outside these hosts, so it always passes this filter.
      if (!el.hasAttribute('data-wc-product-title') &&
          el.closest('[data-wc-component], .wolden-flow-b, .wolden-flow-c, .wolden-customize__modal')) return;
      if (el.textContent !== title) el.textContent = title;
      wrote = true;
    });
    // Last-resort fallback: the page's first h1 (old behavior).
    if (!wrote) {
      var h1 = document.querySelector('h1');
      if (h1) h1.textContent = title;
    }
    // Tab title: preserve the "– Store" suffix when the current title has one.
    var m = document.title.match(/(\s[–—|-]\s.+)$/);
    document.title = m ? (title + m[1]) : title;
  } catch (e) { /* non-fatal — title text only */ }
}

// ─── _swapDescriptionAndJsonLd ────────────────────────────────────────────────
// Monolith lines 2463–2530.
function _swapDescriptionAndJsonLd(handle) {
  if (!handle) return;
  var wc = window.WC;
  if (wc._descriptionFetchInflight) {
    try { wc._descriptionFetchInflight.abort(); } catch (e) { /* ignore */ }
  }
  var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  wc._descriptionFetchInflight = ctrl;
  fetch('/products/' + encodeURIComponent(handle) + '.js', {
    headers: { 'Accept': 'application/json' },
    credentials: 'same-origin',
    signal: ctrl ? ctrl.signal : undefined,
  })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (p) {
      if (!p) return;
      // Title — authoritative from the fetched product JSON. Covers the case
      // where the in-memory orchestrator title was missing (hydration paths
      // don't always populate wc._generations), so the visible title +
      // document.title always land on the swapped child product.
      // (2026-07-02 QA round-1 fix.)
      if (p.title) _setVisibleProductTitle(p.title);
      // Description — common selectors.
      var descEl = document.querySelector(
        '.product__description, [data-product-description], ' +
        '.product-description, .product-information__description, ' +
        'product-information .rte'
      );
      if (descEl && p.description) {
        descEl.innerHTML = p.description;
      }
      // JSON-LD — replace the @type Product script content.
      document.querySelectorAll('script[type="application/ld+json"]').forEach(function (s) {
        try {
          var data = JSON.parse(s.textContent || '{}');
          var isProduct = (data['@type'] === 'Product') ||
                          (Array.isArray(data['@graph']) && data['@graph'].some(function (n) { return n['@type'] === 'Product'; }));
          if (!isProduct) return;
          if (data['@type'] === 'Product') {
            data.name        = p.title || data.name;
            data.description = p.description ? p.description.replace(/<[^>]+>/g, '') : data.description;
            if (p.featured_image) data.image = p.featured_image;
            data.url = window.location.href;
          } else if (Array.isArray(data['@graph'])) {
            data['@graph'].forEach(function (n) {
              if (n['@type'] === 'Product') {
                n.name        = p.title || n.name;
                n.description = p.description ? p.description.replace(/<[^>]+>/g, '') : n.description;
                if (p.featured_image) n.image = p.featured_image;
                n.url = window.location.href;
              }
            });
          }
          s.textContent = JSON.stringify(data);
        } catch (e) { /* ignore individual script parse errors */ }
      });
    })
    .catch(function (e) {
      if (e && e.name === 'AbortError') return;
      log('description fetch failed', e && e.message);
    })
    .finally(function () {
      if (wc._descriptionFetchInflight === ctrl) {
        wc._descriptionFetchInflight = null;
      }
    });
}

// ─── refreshProgressPill ─────────────────────────────────────────────────────
// Monolith lines 2617–2631.
export function refreshProgressPill() {
  var pill = getProgressPill();
  if (!pill) return;
  var wc = window.WC;
  pill.hidden = !wc._minimized;
  if (wc._minimized) {
    var lastUpload = wc._uploads && wc._uploads[wc._uploads.length - 1];
    var petUrl = (lastUpload && lastUpload.publicUrl)
      || (wc._currentResult && wc._currentResult.petImageUrl)
      || '';
    var spinner = pill.querySelector('[data-wc-element="progress-pill-spinner"]');
    if (spinner) {
      spinner.style.backgroundImage = petUrl ? 'url("' + petUrl + '")' : '';
    }
  }
}

// ─── refreshReopenPill ────────────────────────────────────────────────────────
// Monolith lines 1251–1261.
export function refreshReopenPill() {
  var pill = getReopenPill();
  if (!pill) return;
  var wc = window.WC;
  var hasStash = !!(wc._lastResult && wc._lastResult.generationId);
  var hasStoredGens = hasAnyStoredGenForCurrentPdp();
  var modal = getModal();
  var modalIsOpen = modal && !modal.hidden;
  pill.hidden = !((hasStash || hasStoredGens) && !modalIsOpen);
}

// ─── reopenLastResult ─────────────────────────────────────────────────────────
// Monolith lines 1263–1282.
//
// 2026-05-19 — V1→V2 fix: when _lastResult is empty (e.g. after a hard reload
// onto a child PDP, or a friend-share link visit), fall back to State-B
// rehydration via reopenFromStateB so the bottom-right "View Your Art" pill
// actually reopens the modal instead of silently no-op'ing. _lastResult is
// in-memory only (stashed on gen-complete); the fallback reads localStorage
// (per-product namespace) and/or the Bubble-bootstrapped R state.
export function reopenLastResult() {
  var wc = window.WC;
  if (!wc._lastResult) {
    var zone = document.querySelector('[data-wc-component="upload-zone"]');
    if (zone) {
      // Lazy import avoids module-init circular edge (wc14-upload depends
      // on this file). reopenFromStateB handles its own setModalState +
      // showResult re-render.
      import('./wc77-upload.js').then(function (m) {
        try { m.reopenFromStateB(zone); } catch (e) {
          log('reopenLastResult fallback reopenFromStateB error', e && e.message);
        }
      }).catch(function (e) {
        log('reopenLastResult fallback import wc14-upload failed', e && e.message);
      });
    }
    return;
  }
  var stash = wc._lastResult;
  // Remember the customer's last format choice — the stash snapshot is frozen at
  // gen-complete, so it can be stale if they switched format afterward.
  var keepFormat = wc._currentResult && wc._currentResult.activeFormat;
  // Re-hydrate the live result state from the stash before showing.
  wc._currentResult = stash.snapshot || wc._currentResult;
  if (keepFormat && wc._currentResult) wc._currentResult.activeFormat = keepFormat;
  setModalState('result');
  // Re-render the result UI with the stashed data.
  if (stash.orchResult && stash.generationId) {
    // Lazy import avoids module-init circular edge with wc7-result.
    import('./wc77-result.js').then(function (m) {
      try { m.showResult(stash.generationId, stash.orchResult); } catch (e) {
        log('reopenLastResult showResult error', e && e.message);
      }
    }).catch(function (e) {
      log('reopenLastResult import wc7-result failed', e && e.message);
    });
  }
  refreshReopenPill();
  track('result_reopened', { generation_id: stash.generationId });
}

// ─── swapPdpForGeneration ─────────────────────────────────────────────────────
// Monolith lines 2148–2326.
// Called after Phase 1 completes, BEFORE the modal flips to result state.
export function swapPdpForGeneration(orchResult, sourceProductType) {
  try {
    var wc = window.WC;
    var R = wc._currentResult;
    // Decide which child product owns the new PDP based on source type.
    var preferCanvas = /canvas/i.test(sourceProductType || '');
    var newHandle = preferCanvas
      ? (R.canvasShopify && R.canvasShopify[R.activeCanvasSize] && R.canvasShopify[R.activeCanvasSize].shopifyHandle)
      : (R.paintKitShopify && R.paintKitShopify.shopifyHandle);
    var newTitle = preferCanvas
      ? (orchResult.canvas && orchResult.canvas.title)
      : (orchResult.paintKit && orchResult.paintKit.title);

    if (!newHandle) return;

    // 1. URL — INTENTIONALLY DEFERRED to cart-add. Rewriting the URL to a
    //    TEST_DELETE_* child product on Phase 1 means a hard-refresh lands
    //    the customer on the auto-generated child PDP. The URL swap is
    //    handled by applyCleanBreakNow / flushDeferredSwap on modal close.
    //    Only update the document title here (non-destructive, no nav event).
    // 2026-07-02 QA round-1 fix — theme-aware title write (see helper above).
    if (newTitle) _setVisibleProductTitle(newTitle);

    // 2. Gallery — sweep PDP images with the generation's art + mockups.
    //    Rotation: clean art URL + format-appropriate mockups.
    var data = orchResult.data || [];
    var cleanArtUrl = (data[0] && data[0].s3_url) || (data[1] && data[1].s3_url) || '';

    // Build the rotation set for THIS product type.
    var rotation = [cleanArtUrl];
    if (preferCanvas && R.canvasMockups) {
      ['16x20', '24x30', '8x10'].forEach(function (size) {
        if (R.canvasMockups[size]) rotation.push(R.canvasMockups[size]);
      });
    } else {
      if (R.paintKitMockup)  rotation.push(R.paintKitMockup);
      if (R.pbnBwUrl)        rotation.push(R.pbnBwUrl);
      if (R.pbnColoredUrl)   rotation.push(R.pbnColoredUrl);
    }
    rotation = rotation.filter(Boolean);

    if (rotation.length) {
      // Find the main product container. Prefer the one wrapping our upload
      // zone; fall back to <main>. Excludes the related-products section.
      var zone = document.querySelector('[data-wc-component="upload-zone"]');
      var productSection = (zone && zone.closest('main, [data-section-type="main-product"], [data-section-type="product"], section'))
        || document.querySelector('main')
        || document.body;

      var galleryImgs = productSection.querySelectorAll(
        '.product-gallery img, .product-media img, .product__media img, ' +
        '.media-gallery img, [data-product-gallery] img, ' +
        'slideshow-component img, .swiper-slide img, ' +
        'media-gallery img, product-media img'
      );

      var idx = 0;
      var swapped = 0;
      var skipped = 0;
      galleryImgs.forEach(function (img) {
        // Hard exclusion: any image inside related-products / recommendations.
        if (img.closest('.product-recommendations, .product-recommendation, product-recommendations, [data-section-type*="recommend"], [data-section-type*="related"]')) return;
        // Skip the modal/our own UI just in case.
        if (img.closest('[data-wc-component="modal"], [data-wc-component="lightbox"], [data-wc-component="upload-zone"]')) return;

        var nextSrc = rotation[idx % rotation.length];
        // 2026-05-15 — Equality-check guard. Without it, every call writes
        // even when the src already matches, firing the gallery-guard
        // MutationObserver for nothing. Re-runs of swapPdpForGeneration
        // (e.g., from refreshes) become near-free. NB: img.src reads as
        // the canonicalized absolute URL, so we compare on the trailing
        // path/query — gen output URLs are already absolute S3 URLs so
        // they should match img.src exactly post-canonicalization.
        if (img.src !== nextSrc) {
          img.src = nextSrc;
          img.srcset = '';
          // Drop loading="lazy" so the new src renders immediately if
          // the slot was below-the-fold at original-page load.
          img.removeAttribute('loading');
          swapped++;
        } else {
          skipped++;
        }
        idx++;
      });
      log('gallery sweep — swapped', swapped, 'skipped', skipped, 'across', rotation.length, 'rotation slots');
    }

    // 3. Price + variant ID on the underlying PDP's own ATC button.
    //    This is the Phase 11 completion of the Phase 10 swap. Without it,
    //    a customer who closes the modal and clicks the PDP's own ATC button
    //    adds the WRONG variant (the original product's variant). We update
    //    in place — no reload, no Horizon section re-render.
    // 2026-05-19 — Same 2-branch bug as the original updatePdpVariantState.
    // While swapPdpForGeneration isn't currently invoked from the v2 chain
    // (call sites live in legacy wc-upload / wc2-upload only), keep this
    // resolution consistent with updatePdpVariantState so a future caller
    // doesn't trip the same canvas_framed → paint_kit variant landing.
    var newVariantId, pricing;
    if (R.activeFormat === 'canvas_framed') {
      var _cf = R.canvasFramedShopify && R.canvasFramedShopify[R.activeCanvasFramedSize];
      newVariantId = _cf && _cf.variantByColor && _cf.variantByColor[R.activeCanvasFramedColor] || null;
      pricing = (CANVAS_FRAMED_PRICING[R.activeCanvasFramedSize] || null);
    } else if (preferCanvas || R.activeFormat === 'canvas') {
      newVariantId = R.canvasShopify && R.canvasShopify[R.activeCanvasSize] && R.canvasShopify[R.activeCanvasSize].shopifyVariantId;
      pricing = CANVAS_PRICING[R.activeCanvasSize] || null;
    } else {
      newVariantId = R.paintKitShopify && R.paintKitShopify.variants && R.paintKitShopify.variants[R.activePaintKitSize + '_NoFrame'];
      pricing = (PAINT_KIT_PRICING[R.activePaintKitSize] || {})['No Frame'] || null;
    }

    if (newVariantId) {
      // Horizon's ATC component reads variant id from a hidden form input
      // (name="id"), <variant-selects> custom element, or its own data attr.
      // Update every plausible carrier — defensive, not destructive.
      document.querySelectorAll('input[name="id"]').forEach(function (i) {
        i.value = String(newVariantId);
      });
      document.querySelectorAll('[data-product-id], [data-variant-id]').forEach(function (el) {
        if (el.hasAttribute('data-variant-id')) el.setAttribute('data-variant-id', String(newVariantId));
      });
      // Horizon-style add-to-cart custom element holds productData JSON.
      document.querySelectorAll('add-to-cart-component, [data-add-to-cart], buy-buttons-component').forEach(function (el) {
        var pd = el.getAttribute('data-product-id');
        if (pd) el.setAttribute('data-variant-id', String(newVariantId));
      });
    }

    if (pricing) {
      // Match Horizon-style price selectors + common fallbacks. We update the
      // text content; we don't restructure the elements (avoids breaking
      // any theme JS that reads them).
      var fmt = function (n) { return '$' + n.toFixed(2); };
      document.querySelectorAll(
        '.product-price .price, .product__price, [data-product-price], ' +
        '.price__sale, .price-item--sale, .price-item--regular'
      ).forEach(function (el, i) {
        // First pass: assume the first price-shaped element is the sale price.
        if (i === 0) el.textContent = fmt(pricing.sale);
      });
      document.querySelectorAll(
        '.price__compare, .price-item--compare, [data-product-compare-price], s.price-item'
      ).forEach(function (el) { el.textContent = fmt(pricing.compare); });
    }

    // 4. Stamp the body so we can verify the swap in DevTools / debug.
    document.body.setAttribute('data-wc-pdp-snapshot', newHandle);
    log('PDP swapped to', newHandle, '(prefer canvas:', preferCanvas, 'variant:', newVariantId, 'pricing:', pricing + ')');
    track('pdp_swapped', {
      generation_id: orchResult.generationId,
      new_handle: newHandle,
      new_variant_id: newVariantId || null,
      source_type: sourceProductType,
    });
  } catch (err) {
    log('PDP swap failed', err && err.message);
  }
}

// ─── applyCleanBreakNow ───────────────────────────────────────────────────────
// Monolith lines 2327–2371.
export function applyCleanBreakNow(reason) {
  var wc = window.WC;
  var R = wc._currentResult;
  if (!R || !R.generationId) return;
  var newHandle = getCurrentShopifyHandle();
  if (!newHandle) return;
  if (newHandle === wc._lastSwappedHandle) {
    // Same product — only variant ID matters (PBN size/frame change).
    try { updatePdpVariantState(); } catch (e) { /* non-fatal */ }
    return;
  }
  wc._lastSwappedHandle = newHandle;

  // 1. URL — replaceState so back button still goes to previous browse.
  try {
    history.replaceState(null, '', '/products/' + newHandle);
  } catch (e) { /* ignore */ }

  // 2. Title — pulled from the in-memory orchestrator response when available.
  var fmt2 = R.activeFormat;
  var newTitle = '';
  try {
    var lastGen = wc._generations && wc._generations[wc._generations.length - 1];
    if (lastGen) {
      if (fmt2 === 'canvas' && lastGen.canvas && lastGen.canvas.title) newTitle = lastGen.canvas.title;
      else if (fmt2 === 'canvas_framed' && lastGen.canvasFramed && lastGen.canvasFramed.title) newTitle = lastGen.canvasFramed.title;
      else if (fmt2 === 'paint_kit' && lastGen.paintKit && lastGen.paintKit.title) newTitle = lastGen.paintKit.title;
    }
  } catch (e) { /* ignore */ }
  // 2026-07-02 QA round-1 fix — theme-aware title write (see helper). When
  // newTitle is empty here, _swapDescriptionAndJsonLd below still sets it
  // from the fetched product JSON.
  if (newTitle) _setVisibleProductTitle(newTitle);

  // 3. Gallery — sweep with current rotation set per active format.
  _swapGalleryImages(R, fmt2);

  // 4. Variant ID + form properties + price (via updatePdpVariantState).
  try { updatePdpVariantState(); } catch (e) { /* non-fatal */ }

  // 5. Description + JSON-LD — fetch /products/<handle>.js and update DOM.
  _swapDescriptionAndJsonLd(newHandle);

  // 6. Stamp body for debug.
  document.body.setAttribute('data-wc-pdp-snapshot', newHandle);

  // 2026-05-19 — V1→V2 port: re-run initUploadZoneState after the URL swap.
  // The zone's data-wc-product-handle attribute is still the parent's
  // (Liquid wasn't re-rendered) but the URL is now a child PDP, so the
  // `onChildPdpUrl` branch of initUploadZoneState should flip the drop
  // zone from State A → State B. V2 only called this at boot; the post-
  // close re-call was missing, leaving the drop zone stuck on "Drag and
  // drop" on the child PDP after the customer's gen completed.
  try {
    if (window.WC && typeof window.WC.initUploadZoneState === 'function') {
      window.WC.initUploadZoneState();
    }
  } catch (e) { /* non-fatal */ }
  // Ensure PDP selectors visibility is re-evaluated (we may now be on a
  // child PDP URL which qualifies for showSelectors=true even without a
  // pre-existing generationId match against this specific PDP).
  try {
    if (window.WC && typeof window.WC.applyPdpVisibility === 'function') {
      window.WC.applyPdpVisibility();
    }
  } catch (e) { /* non-fatal */ }

  track('clean_break', {
    reason: reason || 'unknown',
    new_handle: newHandle,
    generation_id: R.generationId,
    format: R.activeFormat,
  });
  log('clean break →', newHandle, '(' + reason + ')');
}

// ─── applyCleanBreakDeferred ──────────────────────────────────────────────────
// Monolith lines 2373–2389.
export function applyCleanBreakDeferred(reason) {
  // Modal-driven changes — update variant ID + form properties immediately
  // (so the modal's own mock-up + price reflects), but defer URL/title/
  // gallery/description swap until the modal closes.
  var wc = window.WC;
  var R = wc._currentResult;
  if (!R || !R.generationId) return;
  try { updatePdpVariantState(); } catch (e) { /* non-fatal */ }
  wc._pendingCleanBreak = true;
  wc._pendingCleanBreakReason = reason;
}

// ─── flushDeferredSwap ────────────────────────────────────────────────────────
// Monolith lines 2391–2396.
export function flushDeferredSwap() {
  var wc = window.WC;
  if (!wc._pendingCleanBreak) return;
  var reason = wc._pendingCleanBreakReason || 'modal-close';
  wc._pendingCleanBreak = false;
  wc._pendingCleanBreakReason = null;
  applyCleanBreakNow(reason);
}

// ─── minimizeModalToPill ──────────────────────────────────────────────────────
// Monolith lines 2636–2675.
// Per task spec: "minimal version — just hide modal + show reopen pill, no
// fancy animation." The full fly-to-corner animation is preserved here as a
// comment; Sub-project E can uncomment it.
export function minimizeModalToPill() {
  var modal = getModal();
  if (!modal) return;

  /* Full animation (Sub-project E):
  var panel = modal.querySelector('.wolden-customize__modal-panel');
  if (!panel) return;
  var pillCenterX = window.innerWidth - 22 - 100;
  var pillCenterY = window.innerHeight - 22 - 18;
  var panelRect = panel.getBoundingClientRect();
  var dx = pillCenterX - (panelRect.left + panelRect.width / 2);
  var dy = pillCenterY - (panelRect.top + panelRect.height / 2);
  panel.style.transition = 'transform 0.55s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease-out';
  panel.style.transformOrigin = 'center center';
  void panel.offsetWidth;
  panel.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(0.04)';
  panel.style.opacity = '0';
  setTimeout(function () {
    modal.hidden = true;
    modal.setAttribute('data-wc-modal-state', 'hidden');
    document.body.classList.remove('wolden-customize__body--locked');
    panel.style.transition = '';
    panel.style.transform = '';
    panel.style.opacity = '';
    window.WC._minimized = true;
    refreshProgressPill();
  }, 560);
  */

  // Minimal: hide modal immediately, show progress pill.
  modal.hidden = true;
  modal.setAttribute('data-wc-modal-state', 'hidden');
  document.body.classList.remove('wolden-customize__body--locked');
  window.WC._minimized = true;

  track('generation_minimized', {
    generation_id: (window.WC._currentResult && window.WC._currentResult.generationId) || null,
  });

  refreshProgressPill();
}
